import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {ResizeObserver | undefined} */
  #gridResizeObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#restoreSavedLayout();
    this.#syncAriaPressed();
    this.#clearStuckHiddenCards();
    this.#observeGridWidth();
    this.#applyResponsiveColumns();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridResizeObserver?.disconnect();
  }

  /**
   * Called after section morph updates — re-sync view radios/ARIA after AJAX filters.
   */
  updatedCallback() {
    if (typeof super.updatedCallback === 'function') {
      super.updatedCallback();
    }
    const { grid } = this.refs;
    const view = grid?.getAttribute('product-grid-view') || 'default';
    this.#syncRadioSelection(view);
    this.#syncAriaPressed();
    this.#clearStuckHiddenCards();
    this.#observeGridWidth();
    this.#applyResponsiveColumns();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout with optional view transition.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    try {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } catch {
      this.#setLayout(value);
    } finally {
      this.#clearStuckHiddenCards();
      this.#syncAriaPressed();
      this.#applyResponsiveColumns();
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Applies product-grid-view on the grid.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncRadioSelection(value);
    this.#syncAriaPressed();
    this.#applyResponsiveColumns();
  }

  /**
   * Keep matching radio checked after AJAX morph / programmatic layout changes.
   *
   * @param {string} value
   */
  #syncRadioSelection(value) {
    const desktop = mediaQueryLarge.matches;
    const name = desktop ? 'grid' : 'grid-mobile';
    const radios = this.querySelectorAll(`input[type="radio"][name="${name}"]`);
    for (const radio of radios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      radio.checked = radio.value === value;
    }
  }

  /**
   * Accessibility: mirror checked state onto aria-pressed for the visual controls.
   */
  #syncAriaPressed() {
    const inputs = this.querySelectorAll(
      'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
    );
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const picker = input.nextElementSibling;
      input.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
      if (picker instanceof HTMLElement) {
        picker.toggleAttribute('data-active', input.checked);
      }
    }
  }

  /**
   * Clears leftover content-visibility:hidden from aborted view transitions.
   */
  #clearStuckHiddenCards() {
    const cards = this.querySelectorAll('.product-grid__item');
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      if (card.style.contentVisibility === 'hidden' || card.style.getPropertyValue('content-visibility') === 'hidden') {
        card.style.removeProperty('content-visibility');
      }
      card.style.removeProperty('view-transition-name');
    }
  }

  /**
   * Watch the product-grid container width (remaining space after filters).
   */
  #observeGridWidth() {
    const { grid } = this.refs;
    if (!grid || typeof ResizeObserver === 'undefined') return;

    const target = grid.parentElement instanceof HTMLElement ? grid.parentElement : grid;
    this.#gridResizeObserver?.disconnect();
    this.#gridResizeObserver = new ResizeObserver(() => {
      this.#applyResponsiveColumns();
    });
    this.#gridResizeObserver.observe(target);
  }

  /**
   * Column count based on available grid width + compact/large view.
   * Compact ≈ 170–240px cards; large ≈ max ~320–360px cards.
   *
   * @param {number} width
   * @param {string} view
   * @returns {number}
   */
  #columnCountFor(width, view) {
    const compact = view === 'zoom-out';

    // Mobile density controls: single vs double column only.
    if (!mediaQueryLarge.matches) {
      return view === 'mobile-single' ? 1 : 2;
    }

    if (view === 'mobile-single') return 1;

    if (compact) {
      // Below suitable desktop width, keep existing denser-but-not-6 behavior.
      if (width < 360) return 2;
      if (width < 420) return 2;
      if (width < 720) return 3;
      if (width < 900) return 4;
      // SMALL / zoom-out on suitable desktop: exactly 6 columns.
      return 6;
    }

    // Large / default — below suitable desktop width, keep existing 1–3 columns.
    if (width < 360) return 1;
    if (width < 600) return 2;
    if (width < 860) return 2;
    if (width < 900) return 3;
    // BIG / default on suitable desktop: exactly 5 columns.
    return 5;
  }

  /**
   * Apply column template from available content width.
   */
  #applyResponsiveColumns() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    const measureEl = grid.parentElement instanceof HTMLElement ? grid.parentElement : grid;
    const width = Math.round(measureEl.getBoundingClientRect().width);
    if (!width) return;

    const view = grid.getAttribute('product-grid-view') || 'default';
    const cols = this.#columnCountFor(width, view);

    grid.style.setProperty('grid-template-columns', `repeat(${cols}, minmax(0, 1fr))`);
    grid.style.setProperty('--product-grid-columns-desktop', `repeat(${cols}, minmax(0, 1fr))`);
    grid.dataset.muskleCols = String(cols);
    grid.dataset.muskleGridWidth = String(width);
  }

  /**
   * Restore session preference for the current viewport (without wiping the opposite viewport).
   */
  #restoreSavedLayout() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const stored = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    this.#setLayout(stored);
  }

  /**
   * On crossing the 750px breakpoint, restore THAT viewport's saved layout.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    let stored = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';

    if (!event.matches && stored === 'zoom-out') {
      stored = 'default';
    }
    if (event.matches && stored === 'mobile-single') {
      stored = 'default';
    }

    this.#setLayout(stored);
    this.#clearStuckHiddenCards();
    this.#applyResponsiveColumns();
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
